import { useEffect, useState } from 'react';
import { Icon } from '@/shared/ui';
import { track } from '@/lib/analytics';
import { useTenant } from '@/modules/tenant/presentation/hooks/use-tenant';
import { useGabaritoSubmission } from '@/modules/gabarito/presentation/components/SendGabaritoAction';

type Destino = 'ensaio' | 'gabarito';

/**
 * O convite para trazer colegas — a `InviteSection` do `enamed-feedback-lp`.
 *
 * Lá o convite destravava a nota (a TRI precisava de uma amostra mínima), e a
 * seção vinha com o progresso da comunidade. Aqui a nota é bruta e não depende
 * de amostra: **o que muda com mais gente é o ranking**, que só é
 * representativo se quem fez a prova enviar o gabarito. O progresso da
 * comunidade ficou de fora até haver uma contagem para mostrar.
 *
 * O convite acompanha o momento da edição (`gabaritoSubmission`):
 *
 * - antes da prova, chama para o **ensaio**;
 * - com o envio aberto, chama para o **gabarito** e o ranking;
 * - com o envio encerrado, **some** — ninguém novo entra no ranking.
 */
export function InviteSection() {
  const { exam, edition } = useTenant();
  const submission = useGabaritoSubmission();
  if (submission === 'closed') return null;

  const destino: Destino = submission === 'open' ? 'gabarito' : 'ensaio';
  const prova = `${exam.shortName} ${edition.year}`;

  /**
   * O texto que vai junto com o link. Chega antes do preview, então fala a
   * língua da LP: ensaio e diagnóstico antes da prova; gabarito, nota e
   * ranking depois.
   */
  const shareText =
    destino === 'gabarito'
      ? `Fez a ${prova}? Envie seu gabarito e veja sua nota, sua posição no ranking e onde perdeu ponto:`
      : `Vai fazer a ${prova}? Faça o ensaio e receba um diagnóstico de onde você perde ponto:`;

  return (
    <section className="card invite">
      <div className="invite-layout">
        <div className="invite-copy">
          <h3>
            {destino === 'gabarito'
              ? `Convide quem fez a ${exam.shortName} com você`
              : `Convide colegas para o ensaio da ${exam.shortName}`}
          </h3>
          <p className="card-note">
            {destino === 'gabarito'
              ? `O ranking é montado com os gabaritos enviados: quanto mais gente da ${exam.shortName} participar, mais a sua posição se aproxima da real. Chame quem fez a prova com você.`
              : `Estudar junto rende mais. Chame quem também vai fazer a ${exam.shortName}: depois da prova, todos entram no mesmo ranking.`}
          </p>
        </div>

        <ShareActions shareText={shareText} destino={destino} />
      </div>
    </section>
  );
}

/**
 * Compartilha o endereço do site da prova — nunca a nota, que é da pessoa. No
 * celular abre a folha nativa do sistema; onde não existe `navigator.share`
 * (desktop), copia o convite. O WhatsApp fica ao lado porque é onde as turmas
 * conversam, e no desktop a folha nativa não existe para levar até lá.
 *
 * O link é a **origem da própria página**: cada prova tem o seu site, então o
 * convite da HCPA leva à LP da HCPA sem configuração nenhuma.
 */
function ShareActions({
  shareText,
  destino,
}: {
  shareText: string;
  destino: Destino;
}) {
  const [feedback, setFeedback] = useState<'idle' | 'copiado' | 'erro'>('idle');
  const url = `${window.location.origin}/${destino}`;

  useEffect(() => {
    if (feedback === 'idle') return;
    const timer = setTimeout(() => setFeedback('idle'), 2500);
    return () => clearTimeout(timer);
  }, [feedback]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ text: shareText, url });
        // Só depois do `await`: fechar a folha sem escolher rejeita, e não é
        // convite nenhum.
        track({
          event: 'share',
          method: 'nativo',
          content_type: 'convite',
          item_id: destino,
        });
      } catch {
        // A pessoa fechou a folha de compartilhamento — não é erro.
      }
      return;
    }

    try {
      await navigator.clipboard.writeText(`${shareText} ${url}`);
      setFeedback('copiado');
      track({
        event: 'share',
        method: 'copiar_link',
        content_type: 'convite',
        item_id: destino,
      });
    } catch {
      // Sem permissão de área de transferência (ou fora de contexto seguro).
      setFeedback('erro');
    }
  };

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
    `${shareText} ${url}`,
  )}`;

  return (
    <div className="share-actions">
      <button
        type="button"
        onClick={() => void handleShare()}
        className="cta cta-inline cta-block"
      >
        <Icon size="sm">
          <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
          <path d="m16 6-4-4-4 4" />
          <path d="M12 2v14" />
        </Icon>
        Convidar colegas
      </button>

      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="ghost ghost-block"
        // O clique é o que dá para medir: o WhatsApp abre em outra aba, e se a
        // mensagem saiu de lá a página não tem como saber.
        onClick={() =>
          track({
            event: 'share',
            method: 'whatsapp',
            content_type: 'convite',
            item_id: destino,
          })
        }
      >
        <Icon size="sm">
          <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
        </Icon>
        Enviar no WhatsApp
      </a>

      {feedback !== 'idle' && (
        <p role="status" className={`share-feedback is-${feedback}`}>
          {feedback === 'copiado'
            ? 'Convite copiado! Cole onde quiser.'
            : 'Não foi possível copiar o convite.'}
        </p>
      )}
    </div>
  );
}
